/**
 * Solo/Family plan rules.
 *
 * The Solo plan covers submissions for the membership creator only; the Family
 * plan is required to submit incidents for anyone else on the plan (or to
 * invite a spouse). Mirrors the backend enforcement in
 * MembershipInviteController (spouse invites) — submission gating is
 * frontend-only, matching the existing subscription gate.
 */

export type PatientRef = { patient_name: string; patient_dob: string }

export type CreatorProfile = { full_name: string | null; date_of_birth: string | null } | null | undefined

/**
 * Only the 'solo' tier is limited to creator-only submissions. Every other
 * tier — 'family', legacy 'basic'/'premium', or anything unrecognized — passes
 * the family checks: existing subscribers are grandfathered.
 */
export function isFamilyLimitedTier(tier: string | null | undefined): boolean {
  return tier === 'solo'
}

const norm = (s: string | null | undefined) => (s ?? '').trim().toLowerCase()

/**
 * Whether the patient is the membership creator, using the same name+DOB
 * identity convention as selectDependentForIncident, with whitespace/case
 * normalization to avoid false mismatches on cosmetic differences.
 */
export function isCreatorPatient(patient: PatientRef, creator: CreatorProfile): boolean {
  if (!creator) {
    return false
  }
  return (
    norm(patient.patient_name) === norm(creator.full_name) &&
    norm(patient.patient_dob) === norm(creator.date_of_birth)
  )
}

/**
 * Whether submitting for these patients requires the Family plan (i.e. any
 * patient other than the membership creator).
 *
 * Fail-open: when the creator's profile is unknown (stale persisted state,
 * profile not yet synced) we cannot distinguish, so we do not require Family —
 * never block a Solo subscriber from submitting for themselves.
 */
export function requiresFamilyPlan(patients: PatientRef[], creator: CreatorProfile): boolean {
  if (!creator) {
    return false
  }
  return patients.some((p) => !isCreatorPatient(p, creator))
}
